import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import _, { isNull } from 'lodash';
import { useTranslation } from 'react-i18next';

import { formatDurationCompact } from '../../../config/global';

import { InputSwitch } from 'primereact/inputswitch';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { Button } from '../../../components/ui/Button';
import { Card } from 'primereact/card';

import MediaSettingPopup from './MediaSettingPopup';
import BuilderItemSettingsSheet from './BuilderItemSettingsSheet';
import BuilderDuration from './components/BuilderDuration';
import MediaDuration from './components/MediaDuration';
import MediaTitle from './components/MediaTitle';
import MediaThumb from './components/MediaThumb';
import ImprovedDragHandle from './ImprovedDragHandle';
import PlaylistAccordionRow from './PlaylistAccordionRow';
import { useGalleryContext } from '../../Contexts/GalleryContext';
import { useCustomToast } from '../../Contexts/ToastContext';

import { useDeleteCampaignContent, useDuplicateCampaignContent, useUpdateInteractiveMedia, useUpdateCampaignContentSettings } from '../../../queries';
import { setDeletedItemId, toggleDialog, setDialog } from '../../../redux/Slices/Global/globalSlice';
import { setClockData, setClockDisplay, setClockSettings, setIsEditingClock, setIsEditingWeather, setRssSettings, setSelectedRss, setWeatherBgColor, setWeatherCardColor, setWeatherData, setWeatherTextColor } from '../../../redux/Slices/Apps/appsSlice';
import { handleRssSetting } from '../../../config/global';
import AddFeedDialog from '../../Apps/CustomRss/AddFeedDialog';
import WeatherDialog from '../../Apps/Weather/WeatherDialog';
import ClockDialog from '../../Apps/Clock/ClockDialog';
import PlaylistSettingPopup from './PlaylistSettingPopup';
import { OverlayPanel } from 'primereact/overlaypanel';

function CampaignCard({ item, isActive, progress, isDarkMode = false, isAreaMuted = false, dragHandle = null, index, totalItems, isDragging = false, onPublish, isCompact = false, showDuration = false, centeredDuration = false, areaId, onPreview, builderRow = false, isPlaylistOpen, onTogglePlaylistOpen }) {
    const { t } = useTranslation();
    const { showToast } = useCustomToast();

    const getProgressStyle = (progress, isActive) => {
        // Theme-aware surface token (white in light, #1E293B in dark) so the card
        // never renders white-on-dark regardless of the `isDarkMode` prop being
        // threaded through. Fixes white cards in dark mode centrally.
        if (!isActive) return { background: "var(--pp-bg-elevated)" };

        const percent = Math.max(0, Math.min(100, progress || 0));

        return {
            background: isActive
                ? `linear-gradient(90deg, rgba(99, 102, 241, 0.08) ${percent}%, transparent ${percent}%)`
                : "var(--pp-bg-elevated)",
            transition: 'background 0.1s linear', // Faster transition for smoother updates
            boxShadow: isActive ? "inset 0 0 0 1px rgba(99, 102, 241, 0.2)" : "none"
        };
    };
    const { layer, campaignDetails, deletedItemId, openDialog } = useSelector((state) => state.global);
    const dispatch = useDispatch();
    const { setSelectedItem } = useGalleryContext();

    // F3/F2: the builder's own item-settings sheet, gated by LOCAL state (not
    // the shared openDialog.mediaSettingPopup/playlistSettingPopup flags the
    // old popups use) — and MAIN's current unify state, read once here so the
    // sheet can show its own "unified settings override this item" notice.
    const [builderItemSheet, setBuilderItemSheet] = useState(null);
    const mainUnifyActive = !!campaignDetails?.settings?.live?.layer_settings?.MAIN?.apply_layer_settings;

    /* Open the unified TV-frame preview from anywhere on the thumbnail
     * box. Belt-and-suspenders with MediaThumb's own click handler —
     * the dnd mousedown listener on the parent Draggable could swallow
     * a tap that lands exactly on the inner <button>. When the parent
     * pane supplies `onPreview` we route through its local state and
     * skip Redux entirely; otherwise we fall back to the legacy flag. */
    const openPreview = (e) => {
        e?.stopPropagation();
        e?.preventDefault();
        if (onPreview) {
            onPreview(item);
            return;
        }
        setSelectedItem(item.id);
        dispatch(setDialog({ key: 'previewMedia', value: true }));
    };
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [layoutStatus, setLayoutStatus] = useState()
    const updateInteractiveMedia = useUpdateInteractiveMedia()
    const deleteCampaignContent = useDeleteCampaignContent()
    const duplicateCampaignContent = useDuplicateCampaignContent()
    let adsSettings;
    useEffect(() => {
        if (item.layout_id) {
            const allPublished = item.campaigns.every((screen) => {
                const isScreenPublished = screen.status === "PUBLISHED";
                const areRelatedPublished = Array.isArray(screen.related_screen)
                    ? screen.related_screen.every((rs) => rs.status === "PUBLISHED")
                    : true;

                return isScreenPublished && areRelatedPublished;
            });
            setLayoutStatus(allPublished)
        }
    }, [item])
    if (item.layout_ads_settings && typeof item.layout_ads_settings === 'string') {
        try {
            adsSettings = JSON.parse(item.layout_ads_settings);
        } catch (e) {
        }
    } else if (typeof item.layout_ads_settings === 'object') {
        adsSettings = item.layout_ads_settings;
    }

    //Pop up controllers  
    const [mediaInfoPopup, setMediaInfoPopup] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState({});

    const changeThumb = async (e) => {
        setSelectedMedia(item)
        var formData = new FormData();
        formData.append("thumb", e.target.files[0]);
        formData.append("file_name", item.file_name.slice(0, 20));

        await updateInteractiveMedia.mutateAsync({
            data: formData,
            id: item.id
        }, {
            onSuccess: () => setSelectedMedia({})
        })
    }

    // Delete  Handler
    const removeFromCampaign = async () => {
        dispatch(setDeletedItemId(item.id))
        await deleteCampaignContent.mutateAsync({
            id: item.campaign_playlist_media_id,
            campaign_id: item.campaign_id
        })
        // Spec item F: whole-playlist-row removal (detach from this layer
        // only — CampaignPlaylistRepository::delete() force-deletes just the
        // campaign_playlist_media pivot row, never the playlist/media itself)
        // gets the exact required toast wording — scoped to `builderRow` only
        // (this SAME handler is also reachable from every OTHER DragDrop
        // consumer's legacy, non-builder render — GroupsOrderingDialog,
        // Group.jsx, AreaContentPreview.jsx, InteractiveCampaignSetup.jsx,
        // ScreenInfo.jsx — which must stay zero-diff, so they keep their
        // existing silent behavior).
        if (builderRow && !_.isNull(item.playlist_id)) {
            showToast('success', t('show.show_main_content_title'), t('dragdrop.playlist_removed_from_layer_toast', { name: item.playlist_title }));
        }
    }

    const settingPopupHandler = (data) => {



        let _settings = handleRssSetting(data?.rss_settings, data?.title);
        dispatch(setRssSettings(_settings))
        setSelectedMedia(data)

        if (!isNull(item.playlist_id)) {
            dispatch(toggleDialog("playlistSettingPopup"));
        } else {
            dispatch(toggleDialog("mediaSettingPopup"));
        }
    }

    const addFeedHandler = (item) => {
        dispatch(setSelectedRss(item))
        dispatch(toggleDialog("addCustomFeed"))
    }

    const editWeather = (item) => {
        dispatch(setIsEditingWeather(true))

        let parsedClockSettings;

        parsedClockSettings = typeof item?.settings === 'string'
            ? JSON.parse(item.settings)
            : item?.settings || {};
        const weatherData = {
            id: item.media_id,
            weather: {
                id: item.weather_id,
                title: item.weather_title,
                display: item.display,
                settings: parsedClockSettings,
            }
        }
        dispatch(setWeatherTextColor(parsedClockSettings?.textColor))
        dispatch(setWeatherBgColor(parsedClockSettings?.bgColor))
        dispatch(setWeatherCardColor(parsedClockSettings?.CardColor))
        dispatch(setWeatherData(weatherData))
        dispatch(toggleDialog("WeatherDialog"))
    }


    const editClock = (item) => {

        let parsedClockSettings;

        try {
            parsedClockSettings = typeof item?.clock_settings === 'string'
                ? JSON.parse(item.clock_settings)
                : item?.clock_settings || {};
        } catch (e) {
            parsedClockSettings = {};
        }

        if (!parsedClockSettings.corner_setting || typeof parsedClockSettings.corner_setting !== 'object' || Array.isArray(parsedClockSettings.corner_setting)) {
            parsedClockSettings.corner_setting = {
                start_margin: 0,
                top_margin: 0,
                width: 35,
                height: 37,
                refresh: 0,
            };
        }
        const clockData = {
            id: item.media_id,
            clock: {
                id: item.clock_id,
                title: item.clock_title,
                display: item.clock_display,
                country_code: item.clock_country_code,
                clock_settings: parsedClockSettings,
                zone_name: item.clock_zone_name,

            }
        }
        dispatch(setIsEditingClock(true))
        dispatch(setClockData(clockData))
        dispatch(setClockDisplay(item.clock_display))
        dispatch(setClockSettings(clockData.clock.clock_settings))
        dispatch(toggleDialog("ClockDialog"))
    }

    const duplicateCampaign = async () => {
        await duplicateCampaignContent.mutateAsync({
            campaignMediaId: item.campaign_playlist_media_id,
        })
    }

    const updateCampaignItemSetting = useUpdateCampaignContentSettings();
    const [localIsMuted, setLocalIsMuted] = React.useState(item.is_muted);
    const [localMaintainAspectRatio, setLocalMaintainAspectRatio] = React.useState(item.maintain_aspect_ratio);

    React.useEffect(() => {
        setLocalIsMuted(item.is_muted);
        setLocalMaintainAspectRatio(item.maintain_aspect_ratio);
    }, [item.is_muted, item.maintain_aspect_ratio]);


    const toggleMute = async (e) => {
        e.stopPropagation();
        const newMuteStatus = Number(localIsMuted) === 1 ? 0 : 1;
        setLocalIsMuted(newMuteStatus); // Optimistic update

        const formData = new FormData();
        
        // Replicating MediaSettingPopup logic to ensure safety
        const fields = [
            "show_video_screen", "is_muted", "maintain_aspect_ratio", "is_showed", 
            "downloadable", "show_layer_two", "show_layer_one", "show_layer_three"
        ];
        
        fields.forEach(field => {
            if (field === "is_muted") {
                formData.append(field, newMuteStatus);
            } else {
                 formData.append(field, Number(item[field] ?? 0));
            }
        });

         // Settings JSON
         if (item.media_settings) {
             let settingsVal = item.media_settings;
             if (typeof settingsVal === 'object') settingsVal = JSON.stringify(settingsVal);
             formData.append("settings", settingsVal);
         }

        const updateCache = (newItem) => {
            if (!areaId) return;
            const queryKey = ["getCampaignLayerContent", areaId, "MAIN"];
            const previousData = queryClient.getQueryData(queryKey);
            
            if (previousData) {
                const updatedData = previousData.map(d => 
                    d.campaign_playlist_media_id === item.campaign_playlist_media_id 
                    ? { ...d, ...newItem } 
                    : d
                );
                queryClient.setQueryData(queryKey, updatedData);
            }
            return { previousData };
        };

        const result = updateCache({ is_muted: newMuteStatus });
        const previousData = result?.previousData;

        await updateCampaignItemSetting.mutateAsync({
            data: formData,
            id: item.campaign_playlist_media_id
        }, {
            onError: () => {
                setLocalIsMuted(Number(item.is_muted)); // Revert local state
                if (previousData) {
                    queryClient.setQueryData(["getCampaignLayerContent", areaId, "MAIN"], previousData);
                }
            }
        });
        // Removed onPublish() to avoid full refetch - strictly relying on optimistic update
    }

    const toggleFill = async (e) => {
        e.stopPropagation();
        const newFillStatus = Number(localMaintainAspectRatio) === 0 ? 1 : 0; // 0 is Fill, 1 is Fit
        setLocalMaintainAspectRatio(newFillStatus); 
        // Logic check: Number(localMaintainAspectRatio) === 0 (Fill) ? 1 (Fit) : 0 (Fill).
        // setLocalMaintainAspectRatio should be newFillStatus.

        const formData = new FormData();

        const fields = [
            "show_video_screen", "is_muted", "maintain_aspect_ratio", "is_showed", 
            "downloadable", "show_layer_two", "show_layer_one", "show_layer_three"
        ];
        
        fields.forEach(field => {
             if (field === "maintain_aspect_ratio") {
                formData.append(field, newFillStatus);
            } else {
                 formData.append(field, Number(item[field] ?? 0));
            }
        });

         if (item.media_settings) {
             let settingsVal = item.media_settings;
             if (typeof settingsVal === 'object') settingsVal = JSON.stringify(settingsVal);
             formData.append("settings", settingsVal);
         }
        
        const updateCache = (newItem) => {
             if (!areaId) return;
             const queryKey = ["getCampaignLayerContent", areaId, "MAIN"];
             const previousData = queryClient.getQueryData(queryKey);
             
             if (previousData) {
                 const updatedData = previousData.map(d => 
                     d.campaign_playlist_media_id === item.campaign_playlist_media_id 
                     ? { ...d, ...newItem } 
                     : d
                 );
                 queryClient.setQueryData(queryKey, updatedData);
             }
             return { previousData };
         };
 
        const result = updateCache({ maintain_aspect_ratio: newFillStatus });
        const previousData = result?.previousData;

        await updateCampaignItemSetting.mutateAsync({
            data: formData,
            id: item.campaign_playlist_media_id
        }, {
             onError: () => {
                setLocalMaintainAspectRatio(item.maintain_aspect_ratio); // Revert local state
                if (previousData) {
                     queryClient.setQueryData(["getCampaignLayerContent", areaId, "MAIN"], previousData);
                }
            }
        });
        // Removed onPublish() to avoid full refetch
    }
    const MobileActions = ({ item, forceVisible = false }) => {
        const opRef = React.useRef(null);
        return (
            <div className={forceVisible ? '' : 'md:hidden'}>
                <Button
                    icon="pi pi-ellipsis-v"
                    rounded
                    text
                    className={`p-2 min-w-[44px] min-h-[44px] ${'bg-[var(--pp-bg-tertiary)] text-[var(--pp-text-primary)] hover:opacity-80'}`}
                    aria-label={t('dragdrop.more_actions')}
                    onClick={(e) => opRef.current?.toggle(e)}
                />
                <OverlayPanel ref={opRef} dismissable className="p-3 shadow-lg">
                    <div className="flex flex-col gap-3 min-w-[180px]">
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${'text-[var(--pp-text-secondary)]'}`}>
                            {t('table.actions')}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            {actionBodyTemplate(item)}
                        </div>
                    </div>
                </OverlayPanel>
            </div>
        );
    };

    const actionBodyTemplate = (item) => {
        return (
            <>
                {
                    campaignDetails.is_interactive && layer == "MAIN" &&
                    <>
                        <Tooltip target={`.edit-thumb-${item.id}`} content={t('dragdrop.upload_thumb')} position='bottom' className="fs-8" />
                        <div className="button-wrapper" onClick={() => setSelectedMedia(item)}>
                            <label htmlFor={`${item.id}-upload`} className={`edit-thumb-${item.id} btn p-0 me-2 text-[var(--pp-text-secondary)]`} tabIndex={0}>
                                <i className="bx bx-image-add"></i>
                                <input onChange={changeThumb} type="file" id={`${item.id}-upload`} className="account-file-input" hidden accept="image/png, image/jpeg" />
                            </label>
                        </div>
                    </>
                }
                {
                    item.hasOwnProperty("path") && !JSON.parse(sessionStorage.getItem('is_default_prayer'))
                        ?
                        <>
                            {/* <Tooltip target={`.edit-button-${item.id}`} content="Edit" position='bottom' className="fs-8" />
                                        <button className={`btn bg-transparent p-0 mx-1 edit-button-${item.id}  pt-1 d-block d-md-none`} >
                                            <i onClick={() => setMediaInfoPopup(true)} className="fas fa-pencil text-[var(--pp-text-secondary)]"></i>
                                        </button> */}
                            <Tooltip target={`.setting-button-${item.id}`} content={t('dragdrop.setting')} position='bottom' className="fs-8" />
                            <button className={`btn bg-transparent p-0 mx-1 setting-button-${item.id} pt-1`}  >
                                <i onClick={() => settingPopupHandler(item)} className="fas fa-cog text-[var(--pp-text-secondary)]"></i>
                            </button>
                            {
                                item.type == "RSS_CUSTOM" ?
                                    <>
                                        {/* Add Feeds RSS */}
                                        <Tooltip target={`.add-feed-button-${item.id}`} showDelay={100} className="fs-8" content={t('dragdrop.add_feed_content')} position='bottom' />
                                        <button className={`btn bg-transparent p-0 mx-1 add-feed-button-${item.id} pt-1`} onClick={() => addFeedHandler(item)} >
                                            <i className='bx bx-add-to-queue text-[var(--pp-text-secondary)]'></i>
                                        </button>
                                    </>
                                    :
                                    ""
                            }
                            {
                                item.type == "WEATHER" ?
                                    <>
                                        <Tooltip target={`.weather-button-${item.id}`} showDelay={100} className="fs-8" content={t('dragdrop.edit_weather')} position='bottom' />
                                        <button className={`btn bg-transparent p-0 mx-1 weather-button-${item.id} pt-1`} onClick={() => editWeather(item)} >
                                            <i className='bx bx-pen text-[var(--pp-text-secondary)]'></i>
                                        </button>
                                    </>
                                    :
                                    ""
                            }
                            {
                                item.type == "CLOCK" ?
                                    <>
                                        <Tooltip target={`.clock-button-${item.id}`} showDelay={100} className="fs-8" content={t('dragdrop.edit_clock')} position='bottom' />
                                        <button className={`btn bg-transparent p-0 mx-1 clock-button-${item.id} pt-1`} onClick={() => editClock(item)} >
                                            <i className='bx bx-pen text-[var(--pp-text-secondary)]'></i>
                                        </button>
                                    </>
                                    :
                                    ""
                            }
                        </>
                        :
                        ""
                }


                <Tooltip target={`.duplicate-button-${item.id}`} content={t('dragdrop.duplicate')} position='bottom' className="fs-8" />
                <Button
                    loading={duplicateCampaignContent.isLoading}
                    icon="fa-regular fa-clone text-[var(--pp-text-secondary)]"
                    className={`btn bg-transparent p-0 mx-1 w-auto border-0 pt-1 duplicate-button-${item.id}`}
                    onClick={duplicateCampaign}
                />
                {
                    !JSON.parse(sessionStorage.getItem('is_default_prayer')) &&
                    <>
                        <Tooltip target={`.delete-button-${item.id}`} content={t('buttons.delete')} position='bottom' className="fs-8" />
                        {
                            deletedItemId === item.id && deleteCampaignContent.isLoading ?
                                <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="3" fill="var(--surface-ground)" className='mx-1' animationDuration="2s" />
                                :
                                <button className={`btn bg-transparent p-0 mx-1 delete-button-${item.id}`} onClick={removeFromCampaign} >
                                    <i className="bx bx-trash  text-[var(--pp-text-secondary)]"></i>
                                </button>
                        }
                    </>
                }
            </>
        )
    }
    const showDragHandle = !!dragHandle;

    // Helper function to get the mobile title
    const getMobileTitle = () => {
        if (!_.isNull(item.playlist_id)) return item.playlist_title;
        if (!_.isNull(item.layout_id)) return item.layout_title;
        if (item.type === "RSS" || item.type === "RSS_CUSTOM") return item.file_name?.split(".")[0];
        return item.file_name;
    };

    // ─── builderRow: the Layer-3 campaign builder's single-line item row,
    // ported pixel-for-pixel from .claude/pp-campaign-builder-prototype.html
    // (.item/.thumb/.it-txt/.it-actions). Scoped via this prop + the
    // pp-cb-item* classes (setup.css) so every OTHER CampaignCard consumer
    // (unaffected — they never pass builderRow) stays exactly as it was.
    // Reuses the SAME handlers/state as the default render below — no new
    // data logic, purely a different DOM shape for the same item. ───────
    if (builderRow) {
        const builderTitle = !_.isNull(item.playlist_id) ? item.playlist_title
            : !_.isNull(item.layout_id) ? item.layout_title
            : (item.type === "RSS" || item.type === "RSS_CUSTOM") ? item.file_name?.split(".")[0]
            : item.file_name;

        const builderTypeLabel = !_.isNull(item.playlist_id) ? t('dragdrop.playlist_label')
            : !_.isNull(item.layout_id) ? t('show.show_type_layout')
            : item.type === "VIDEO" ? t('show.show_type_video')
            : item.type === "IMAGE" ? t('show.show_type_image')
            : item.type === "AUDIO" ? t('show.show_type_audio')
            : t('show.show_type_other');

        const builderThumb = !_.isNull(item.playlist_id) ? (
            <i className="fas fa-list-ul" style={{ color: 'var(--pp-btn-honey)' }} />
        ) : !_.isNull(item.layout_id) ? (
            <i className="bx bx-layout" style={{ fontSize: 20, color: 'var(--pp-btn-honey)' }} />
        ) : ["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(item.type) ? (
            <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: 'var(--pp-btn-info-text)' }}>
                {item.type.replace(/_/g, ' ')}
            </span>
        ) : item.type === "WEATHER" ? (
            <i className="fa-solid fa-cloud-sun" style={{ color: 'var(--pp-btn-info-text)' }} />
        ) : item.type === "CLOCK" ? (
            <i className="fa-solid fa-clock" style={{ color: '#F97316' }} />
        ) : (
            <MediaThumb item={item} onPreview={onPreview} />
        );

        const isPlaylistItem = !_.isNull(item.playlist_id);
        const canDelete = !JSON.parse(sessionStorage.getItem('is_default_prayer'));
        const isDeleting = deletedItemId === item.id && deleteCampaignContent.isLoading;

        // F6: tap-to-edit duration restored for every MAIN media row (audio/
        // video/image/etc — not playlist/layout references, which carry an
        // AGGREGATE duration with no single row to edit against; matches the
        // prototype's own `it.d` gate, which only individual audio/video mock
        // items carry). Uses the SAME endpoint/hook MediaDuration.jsx already
        // does (PUT /campaign/playlist-media/{id}/update), just restyled to
        // the compact .pp-cb-dur look.
        const builderDurationNode = !_.isNull(item.playlist_id) ? (
            <span className="num">{formatDurationCompact(item.playlist_duration)}</span>
        ) : !_.isNull(item.layout_id) ? (
            <span className="num">{formatDurationCompact(item.layout_duration)}</span>
        ) : (
            <BuilderDuration
                seconds={item.total_duration}
                editable
                source="campaign"
                id={item.campaign_playlist_media_id}
                layer={item.layer || 'MAIN'}
            />
        );

        const builderThumbAndText = (
            <>
                <div className="pp-cb-item-thumb">{builderThumb}</div>
                <div className="pp-cb-item-txt">
                    <span className="n">{builderTitle}</span>
                    <span className="m">{builderTypeLabel} · {builderDurationNode}</span>
                </div>
            </>
        );

        // Spec item A/C: every playlist row on MAIN becomes an accordion —
        // folds open in place instead of navigating away to the legacy
        // /playlist/:id/show page (that page/route is untouched, still
        // reachable from the library, this just removes the BUILDER's own
        // link into it). Header actions (mute/settings/delete) are the SAME
        // handlers the non-playlist branch below already uses — only the
        // body (sub-item list) and the open/fold toggle are new.
        if (isPlaylistItem) {
            return (
                <>
                    <PlaylistAccordionRow
                        row={item}
                        layer="MAIN"
                        campaignId={item.campaign_id}
                        open={isPlaylistOpen ? isPlaylistOpen(item.playlist_id) : true}
                        onToggleOpen={() => onTogglePlaylistOpen?.(item.playlist_id)}
                        gripNode={showDragHandle && (
                            <div className="pp-cb-item-grip">
                                <ImprovedDragHandle itemId={item.id} index={index} totalItems={totalItems} dragHandleProps={dragHandle} />
                            </div>
                        )}
                        thumbNode={builderThumb}
                        metaKind="item"
                        allDurationsEditable
                        toastTitle={t('show.show_main_content_title')}
                        headerActions={
                            <>
                                <button
                                    className={`pp-cb-chipbtn ${Number(localIsMuted) !== 1 ? 'on' : ''}`}
                                    onClick={toggleMute}
                                    title={Number(localIsMuted) === 1 ? t('dragdrop.unmute') : t('dragdrop.mute')}
                                >
                                    <i className={`fa-solid ${Number(localIsMuted) === 1 ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                                </button>
                                <button
                                    className="pp-cb-chipbtn"
                                    onClick={() => setBuilderItemSheet({ item, isPlaylist: true })}
                                    title={t('dragdrop.setting')}
                                >
                                    <i className="pi pi-cog" />
                                </button>
                                {canDelete && (
                                    <button
                                        className="pp-cb-chipbtn del"
                                        onClick={removeFromCampaign}
                                        disabled={isDeleting}
                                        title={t('buttons.delete')}
                                    >
                                        {isDeleting ? (
                                            <ProgressSpinner style={{ width: 16, height: 16 }} strokeWidth="4" fill="var(--surface-ground)" animationDuration="2s" />
                                        ) : (
                                            <i className="bx bx-trash" />
                                        )}
                                    </button>
                                )}
                                <MobileActions item={item} forceVisible />
                            </>
                        }
                    />
                    {builderItemSheet && builderItemSheet.item.campaign_playlist_media_id === item.campaign_playlist_media_id ? (
                        <BuilderItemSettingsSheet
                            item={builderItemSheet.item}
                            layer="MAIN"
                            isPlaylist={builderItemSheet.isPlaylist}
                            unifyActive={mainUnifyActive}
                            onClose={() => setBuilderItemSheet(null)}
                        />
                    ) : <></>}
                    {openDialog.addCustomFeed ? <AddFeedDialog /> : <></>}
                    {openDialog.WeatherDialog ? <WeatherDialog /> : <></>}
                    {openDialog.ClockDialog ? <ClockDialog /> : <></>}
                </>
            );
        }

        return (
            <>
                <div className="pp-cb-item">
                    {showDragHandle && (
                        <div className="pp-cb-item-grip">
                            <ImprovedDragHandle itemId={item.id} index={index} totalItems={totalItems} dragHandleProps={dragHandle} />
                        </div>
                    )}
                    {builderThumbAndText}
                    <div className="pp-cb-item-actions">
                        <button
                            className={`pp-cb-chipbtn ${Number(localIsMuted) !== 1 ? 'on' : ''}`}
                            onClick={toggleMute}
                            title={Number(localIsMuted) === 1 ? t('dragdrop.unmute') : t('dragdrop.mute')}
                        >
                            <i className={`fa-solid ${Number(localIsMuted) === 1 ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                        </button>
                        {/* Fill/Fit is no longer a standalone row chip — it now lives
                            only inside MediaSettingPopup's "طريقة ملء الشاشة" control
                            (fit/crop/fill), reached via the gear button below. */}
                        <button
                            className="pp-cb-chipbtn"
                            onClick={() => setBuilderItemSheet({ item, isPlaylist: isPlaylistItem })}
                            title={t('dragdrop.setting')}
                        >
                            <i className="pi pi-cog" />
                        </button>
                        {canDelete && (
                            <button
                                className="pp-cb-chipbtn del"
                                onClick={removeFromCampaign}
                                disabled={isDeleting}
                                title={t('buttons.delete')}
                            >
                                {isDeleting ? (
                                    <ProgressSpinner style={{ width: 16, height: 16 }} strokeWidth="4" fill="var(--surface-ground)" animationDuration="2s" />
                                ) : (
                                    <i className="bx bx-trash" />
                                )}
                            </button>
                        )}
                        <MobileActions item={item} forceVisible />
                    </div>
                </div>
                {builderItemSheet && builderItemSheet.item.campaign_playlist_media_id === item.campaign_playlist_media_id ? (
                    <BuilderItemSettingsSheet
                        item={builderItemSheet.item}
                        layer="MAIN"
                        isPlaylist={builderItemSheet.isPlaylist}
                        unifyActive={mainUnifyActive}
                        onClose={() => setBuilderItemSheet(null)}
                    />
                ) : <></>}
                {openDialog.addCustomFeed ? <AddFeedDialog /> : <></>}
                {openDialog.WeatherDialog ? <WeatherDialog /> : <></>}
                {openDialog.ClockDialog ? <ClockDialog /> : <></>}
            </>
        );
    }

    // Helper function for mobile status badges
    const getMobileStatusBadges = () => (
        <>
            {Number(item.is_muted) === 1 && (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${'bg-red-500/10 text-[var(--pp-danger)]'}`}>
                    <i className="fa-solid fa-volume-xmark"></i>
                </span>
            )}
            {Number(item.maintain_aspect_ratio) === 0 && (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${'bg-[var(--pp-primary-subtle)] text-[var(--pp-primary)]'}`}>
                    <i className="bx bx-expand"></i>
                </span>
            )}
        </>
    );

    // Helper function for publish warning
    const renderPublishWarning = () => {
        // ⚠️ has_unpublished_changes, not item.status — status is the
        // playlist's own INACTIVE column, forced dirty by an observer on any
        // media write regardless of real publish state (same bug as the
        // Library gallery had). See PlaylistController for the comparison.
        if (item.playlist_id && item.has_unpublished_changes === true) {
            return (
                <div className={`flex items-center gap-1.5 ml-auto text-xs ${'text-[var(--pp-warning)]'}`}>
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <span>{t('dragdrop.publish_required')}</span>
                </div>
            );
        }
        if (item.layout_id && !layoutStatus) {
            return (
                <div className={`flex items-center gap-1.5 ml-auto text-xs ${'text-[var(--pp-warning)]'}`}>
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <span>{t('dragdrop.publish_required')}</span>
                </div>
            );
        }
        return null;
    };

    // Helper function for mobile thumbnail
    const renderMobileThumb = () => {
        const thumbSize = isCompact ? 'w-[48px] h-[48px]' : 'w-[56px] h-[56px]';

        if (!_.isNull(item.playlist_id)) {
            return (
                <div
                    onClick={() => navigate(`/playlist/${item.id}/show`)}
                    className={`cursor-pointer ${thumbSize} rounded-lg flex items-center justify-center ${'bg-[var(--pp-bg-tertiary)]'}`}
                >
                    <i className={`fas fa-list-ul text-lg ${'text-[var(--pp-text-secondary)]'}`}></i>
                </div>
            );
        }

        if (!_.isNull(item.layout_id)) {
            return (
                <div
                    onClick={() => adsSettings?.is_animated_layout ? navigate(`/apps/animated_layouts/${item.id}/show`) : navigate(`/apps/layouts/${item.id}/show`)}
                    className={`cursor-pointer ${thumbSize} rounded-lg flex items-center justify-center ${'bg-[var(--pp-bg-tertiary)] hover:opacity-80'}`}
                >
                    <i className={`bx bx-layout text-xl ${'text-[var(--pp-text-secondary)]'}`}></i>
                </div>
            );
        }

        if (["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(item.type)) {
            return (
                <div className={`${thumbSize} rounded-lg flex items-center justify-center p-1 border shadow-sm relative overflow-hidden ${'bg-[var(--pp-bg-tertiary)] border-[var(--pp-border)]'}`}>
                    <span className={`text-[9px] uppercase font-bold text-center leading-tight tracking-wider ${'text-[var(--pp-primary)]'}`}>
                        {(item.type).replace(/_/g, " ")}
                    </span>
                    {(isAreaMuted || Number(item.is_muted) === 1) && (
                        <div className="absolute top-0.5 right-0.5 z-10 bg-black/70 rounded-full w-5 h-5 flex items-center justify-center">
                            <i className="fa-solid fa-volume-xmark text-white text-[10px]"></i>
                        </div>
                    )}
                </div>
            );
        }

        if (item.type === "WEATHER") {
            return (
                <div className={`${thumbSize} rounded-lg flex items-center justify-center bg-gradient-to-br ${'from-[var(--pp-bg-tertiary)] to-[var(--pp-bg-tertiary)]'}`}>
                    <i className={`fa-solid fa-cloud-sun text-xl drop-shadow-sm ${'text-[var(--pp-info)]'}`}></i>
                </div>
            );
        }

        if (item.type === "CLOCK") {
            return (
                <div className={`${thumbSize} rounded-lg flex items-center justify-center bg-gradient-to-br ${'from-[var(--pp-bg-tertiary)] to-[var(--pp-bg-tertiary)]'}`}>
                    <i className={`fa-solid fa-clock text-xl drop-shadow-sm ${'text-[#F97316]'}`}></i>
                </div>
            );
        }

        return (
            <div onClick={openPreview} className={`${thumbSize} rounded-lg overflow-hidden relative flex items-center justify-center shadow-inner cursor-pointer ${'bg-[var(--pp-bg-tertiary)]'}`}>
                {updateInteractiveMedia.isLoading && item.id === selectedMedia?.id ? (
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration="2s" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <MediaThumb item={item} onPreview={onPreview} />
                    </div>
                )}
                {(isAreaMuted || Number(item.is_muted) === 1) && (
                    <div className="absolute top-0.5 right-0.5 z-10 bg-black/70 rounded-full w-5 h-5 flex items-center justify-center">
                        <i className="fa-solid fa-volume-xmark text-white text-[10px]"></i>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={`w-100 mb-2 ${isDragging ? 'dragging' : ''}`}>
                <div
                    style={getProgressStyle(progress, isActive)}
                    className={`
                        w-100 relative overflow-hidden transition-all duration-300 group
                        rounded-xl border
                        ${isActive
                            ? ('border-indigo-500/50 shadow-[0_0_15px_-3px_rgba(99,102,241,0.2)]')
                            : ('bg-[var(--pp-bg-secondary)] border-[var(--pp-border)] shadow-sm')
                        }
                    `}
                >
                    {/* Mobile Layout (< md breakpoint) */}
                    <div className={`md:hidden ${isCompact ? 'p-2' : 'p-3'}`}>
                        {/* Mobile Header Row - Drag Handle + Thumbnail + Title + Kebab */}
                        <div className="flex items-center gap-3">
                            {/* Drag Handle for Mobile - Icon only, no position indicator */}
                            {showDragHandle && (
                                <div className="flex-shrink-0 touch-none">
                                    <div
                                        className="drag-handle-icon w-[40px] h-[40px] flex items-center justify-center cursor-grab active:cursor-grabbing rounded-md hover:bg-indigo-500/10"
                                        {...(dragHandle || {})}
                                    >
                                        <i className={`fas fa-bars ${'text-[var(--pp-text-secondary)]'}`}></i>
                                    </div>
                                </div>
                            )}

                            {/* Thumbnail */}
                            <div className="flex-shrink-0">
                                {renderMobileThumb()}
                            </div>

                            {/* Title & Info */}
                            <div className="flex-1 min-w-0">
                                <div className={`font-medium truncate ${isCompact ? 'text-sm' : 'text-base'} ${'text-[var(--pp-text-primary)]'}`}>
                                    {getMobileTitle()}
                                </div>
                                <div className={`flex items-center gap-2 mt-1 ${isCompact ? 'text-xs' : 'text-sm'} ${'text-[var(--pp-text-secondary)]'}`}>
                                    <span className="font-mono flex items-center gap-1">
                                        <i className="fa-regular fa-clock text-[10px]"></i>
                                        <MediaDuration item={item} isDarkMode={isDarkMode} simple={true}/>
                                    </span>
                                    {getMobileStatusBadges()}
                                </div>
                            </div>

                            {/* Kebab Menu */}
                            <MobileActions item={item} />
                        </div>

                        {/* Mobile Quick Actions Row */}
                        {!isCompact && (
                            <div className={`flex items-center gap-3 mt-3 pt-3 border-t ${'border-[var(--pp-border)]'}`}>
                                {/* Position Indicator / Queue Placement */}
                                {showDragHandle && (
                                    <div className={`flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-medium ${'bg-[var(--pp-bg-tertiary)] text-[var(--pp-text-secondary)]'}`}>
                                        <span>{index + 1}/{totalItems}</span>
                                    </div>
                                )}

                                {/* Mute Toggle */}
                                <button
                                    onClick={toggleMute}
                                    disabled={false}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all min-h-[44px] ${
                                        Number(item.is_muted) === 1
                                            ? ('bg-red-500/10 text-[var(--pp-danger)]')
                                            : ('bg-[var(--pp-bg-tertiary)] text-[var(--pp-text-secondary)] hover:opacity-80')
                                    }`}
                                >
                                    <i className={`fa-solid ${Number(item.is_muted) === 1 ? 'fa-volume-xmark' : 'fa-volume-high'} text-sm`}></i>
                                    <span className="text-xs font-medium">{Number(item.is_muted) === 1 ? t('dragdrop.muted') : t('dragdrop.sound')}</span>
                                </button>

                                {/* Fill/Fit Toggle */}
                                {["IMAGE", "VIDEO"].includes(item.type) && (
                                    <button
                                        onClick={toggleFill}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all min-h-[44px] ${
                                            Number(item.maintain_aspect_ratio) === 0
                                                ? ('bg-[color-mix(in_srgb,var(--pp-btn-honey)_10%,transparent)] text-[var(--pp-btn-honey)]')
                                                : ('bg-[var(--pp-bg-tertiary)] text-[var(--pp-text-secondary)] hover:opacity-80')
                                        }`}
                                    >
                                        <i className={`bx ${Number(item.maintain_aspect_ratio) === 0 ? 'bx-collapse' : 'bx-expand'} text-sm`}></i>
                                        <span className="text-xs font-medium">{Number(item.maintain_aspect_ratio) === 0 ? t('dragdrop.fill') : t('dragdrop.fit')}</span>
                                    </button>
                                )}

                                {/* Publish Required Warning */}
                                {renderPublishWarning()}
                            </div>
                        )}
                    </div>

                    {/* Desktop Layout (>= md breakpoint) */}
                    <div className={`hidden md:block ${isCompact ? "p-2" : "p-3"}`}>
                        <div className={isCompact ? "flex flex-row items-center gap-3" : "d-flex align-items-center gap-4"}>
                        {showDragHandle && (
                            <div className={isCompact ? "absolute top-1 left-1 z-20 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing" : "flex-shrink-0"}>
                                <ImprovedDragHandle
                                    itemId={item.id}
                                    index={index}
                                    totalItems={totalItems}
                                    dragHandleProps={dragHandle}
                                />
                            </div>
                        )}
                        
                        {/* ------------Media Thumb-------- */}
                        {
                            !(_.isNull(item.playlist_id)) ?
                                <div onClick={() => navigate(`/playlist/${item.id}/show`)} className={`cursor-pointer rounded-lg flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${isCompact ? 'w-[42px] h-[42px] text-xs bg-gray-100 dark:bg-slate-700' : 'col-1 me-2 p-4 bg-secondary'}`}>
                                    <i className={`fas fa-list-ul ${isCompact ? 'text-sm' : 'fs-4'} text-white`}></i>
                                </div>
                                :
                                !(_.isNull(item.layout_id)) ?
                                    <div onClick={() => adsSettings.is_animated_layout ? navigate(`/apps/animated_layouts/${item.id}/show`) : navigate(`/apps/layouts/${item.id}/show`)} className={`cursor-pointer rounded-lg flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${isCompact ? 'w-[42px] h-[42px]' : 'w-[72px] h-[72px]'} ${'bg-[var(--pp-bg-tertiary)] text-[var(--pp-text-secondary)] hover:text-[var(--pp-btn-honey)]'}`}>
                                        <i className={`bx bx-layout ${isCompact ? 'text-xl' : 'fs-1'}`}></i>
                                    </div>
                                    :
                                    <>
                                        {
                                            ["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(item.type) ?
                                                <div className={`cursor-pointer rounded-lg flex-shrink-0 flex items-center justify-center p-1 border shadow-sm relative overflow-hidden ${isCompact ? 'w-[42px] h-[42px]' : 'w-[72px] h-[72px]'} ${'bg-[var(--pp-bg-tertiary)] border-[var(--pp-border)]'}`}>
                                                    {item.thumb ? (
                                                        <div className="w-full h-full flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500">
                                                            <MediaThumb item={item} onPreview={onPreview} />
                                                        </div>
                                                    ) : (
                                                        <span className={`text-[${isCompact ? '8px' : '10px'}] uppercase font-bold text-center leading-tight tracking-wider ${'text-[var(--pp-primary)]'}`}>{(item.type).replace(/_/g, " ")}</span>
                                                    )}
                                                    {(isAreaMuted || Number(localIsMuted) === 1) && (
                                                        <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center backdrop-blur-[1px] pointer-events-none">
                                                            <i className={`fa-solid fa-volume-xmark text-white drop-shadow-md ${isCompact ? 'text-xs' : 'text-lg'}`}></i>
                                                        </div>
                                                    )}
                                                </div>
                                                :
                                                <div onClick={openPreview} className={`${isCompact ? 'w-[42px] h-[42px]' : 'w-[72px] h-[72px]'} rounded-lg overflow-hidden relative flex-shrink-0 flex items-center justify-center shadow-inner cursor-pointer ${'bg-[var(--pp-bg-tertiary)]'}`}>
                                                    {
                                                        updateInteractiveMedia.isLoading && item.id === selectedMedia?.id ?
                                                            <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration="2s" />
                                                            :
                                                            <>
                                                                {item.type === "WEATHER" ? (
                                                                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${'from-[var(--pp-bg-tertiary)] to-[var(--pp-bg-tertiary)]'}`}>
                                                                        <i className={`fa-solid fa-cloud-sun ${isCompact ? 'text-lg' : 'fs-2'} drop-shadow-sm ${'text-[var(--pp-info)]'}`}></i>
                                                                    </div>
                                                                ) : item.type === "CLOCK" ? (
                                                                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${'from-[var(--pp-bg-tertiary)] to-[var(--pp-bg-tertiary)]'}`}>
                                                                        <i className={`fa-solid fa-clock ${isCompact ? 'text-lg' : 'fs-2'} drop-shadow-sm ${'text-[#F97316]'}`}></i>
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500">
                                                                        <MediaThumb item={item} onPreview={onPreview} />
                                                                    </div>
                                                                )}
                                                            </>
                                                    }
                                                    {/* Mute Overlay */}
                                                    {(isAreaMuted || Number(localIsMuted) === 1) && (
                                                        <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center backdrop-blur-[1px] pointer-events-none">
                                                            <i className={`fa-solid fa-volume-xmark text-white drop-shadow-md ${isCompact ? 'text-xs' : 'text-lg'}`}></i>
                                                        </div>
                                                    )}
                                                </div>
                                        }
                                    </>
                        }

                        {/* ------------Content & Controls-------- */}
                        {
                            campaignDetails.is_interactive ?
                                <MediaTitle item={item} isDarkMode={isDarkMode} />
                                :
                                <div className={isCompact ? 'flex flex-col flex-1 min-w-0 gap-1' : 'flex flex-col flex-1 min-w-0 px-2'}>
                                    {/* Title Row */}
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                         <div className={`text-truncate font-medium ${isCompact ? 'text-xs' : 'w-full text-sm'} ${'text-[var(--pp-text-primary)]'}`}>
                                            {
                                                !(_.isNull(item.playlist_id)) ? item.playlist_title : !(_.isNull(item.layout_id)) ? item.layout_title : item.type == "RSS" || item.type == "RSS_CUSTOM" ? item.file_name?.split(".")[0] : item.file_name
                                            }
                                        </div>
                                        {/* Duration for Compact - integrated in top right */}
                                        {isCompact && !showDuration && (
                                            <span className={`text-[10px] ml-2 flex-shrink-0 font-mono opacity-70 ${'text-[var(--pp-text-secondary)]'}`}>
                                                 <MediaDuration item={item} isDarkMode={isDarkMode} simple={true}/>
                                            </span>
                                        )}
                                    </div>

                                    {/* Standard Mode Status Badges */}
                                    {!isCompact && (
                                    <div className="flex items-center gap-2 mt-1">
                                         {Number(localIsMuted) === 1 && (
                                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${'bg-red-500/10 text-[var(--pp-danger)] border-[var(--pp-border)]'}`}>
                                                <i className="fa-solid fa-volume-xmark"></i>
                                                <span>{t('dragdrop.muted')}</span>
                                            </div>
                                        )}
                                        {Number(localMaintainAspectRatio) === 0 && (
                                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${'bg-[var(--pp-primary-subtle)] text-[var(--pp-primary)] border-[var(--pp-border)]'}`}>
                                                <i className="bx bx-expand"></i>
                                                <span>{t('dragdrop.fill')}</span>
                                            </div>
                                        )}
                                    </div>
                                    )}

                                    {/* Compact Toolbar */}
                                    {isCompact && (
                                        <div className="flex items-center gap-3 mt-0.5">
                                            {/* Mute Icon Toggle */}
                                             <button 
                                                onClick={toggleMute}
                                                disabled={false}
                                                className={`text-[10px] flex items-center gap-1 hover:brightness-110 transition-all ${Number(localIsMuted) === 1 ? 'text-red-500 font-medium' : ('text-[var(--pp-text-secondary)] hover:text-[var(--pp-text-primary)]')}`}
                                                title={Number(localIsMuted) === 1 ? t('dragdrop.unmute') : t('dragdrop.mute')}
                                            >
                                                <i className={`fa-solid ${Number(localIsMuted) === 1 ? 'fa-volume-xmark' : 'fa-volume-high'}`}></i>
                                                <span className="text-[9px] uppercase tracking-wide">{Number(localIsMuted) === 1 ? t('dragdrop.muted') : t('dragdrop.mute')}</span>
                                            </button>

                                            {/* Fill Icon Toggle */}
                                            {(["IMAGE", "VIDEO"].includes(item.type) || !_.isNull(item.playlist_id)) && (
                                                 <button 
                                                    onClick={toggleFill}
                                                    className={`text-[10px] flex items-center gap-1 hover:brightness-110 transition-all ${Number(localMaintainAspectRatio) === 0 ? 'text-indigo-500 font-medium' : ('text-[var(--pp-text-secondary)] hover:text-[var(--pp-text-primary)]')}`}
                                                    title={Number(localMaintainAspectRatio) === 0 ? t('dragdrop.fit_to_screen') : t('dragdrop.fill_to_screen')}
                                                >
                                                    <i className={`bx ${Number(localMaintainAspectRatio) === 0 ? 'bx-collapse' : 'bx-expand'}`}></i>
                                                    <span className="text-[9px] uppercase tracking-wide">{Number(localMaintainAspectRatio) === 0 ? t('dragdrop.fill') : t('dragdrop.fit')}</span>
                                                </button>
                                            )}

                                            {/* Actions */}
                                            <div className="ml-auto flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity">
                                                 <div className="flex items-center gap-1 scale-90 origin-right">
                                                     {actionBodyTemplate(item)}
                                                 </div>
                                            </div>
                                        </div>
                                    )}

                                    {item.playlist_id && item.has_unpublished_changes === true ?
                                        <div className=" gap-1 text-muted text-[12px] flex justify-start items-center" >
                                            <i className="draftedItem cursor-default fa-solid fa-circle-exclamation"></i>
                                            <div className="draftedItem cursor-default">{t('dragdrop.publish_required')}</div>
                                        </div>
                                        : item.layout_id && !layoutStatus ?
                                            <div className=" gap-1 text-muted text-[12px] flex justify-start items-center" >
                                                <i className="draftedItem cursor-default fa-solid fa-circle-exclamation"></i>
                                                 <div className="draftedItem cursor-default">{t('dragdrop.publish_required')}</div>
                                            </div>
                                            : ""}
                                            
                                    <Tooltip target={`.draftedItem`} showDelay={100} className="fs-8" />
                                </div>

                        }

                        {/* -------Standard Duration------- */}
                        {showDuration && (
                        <div className={`px-3 flex flex-col items-center justify-center ${centeredDuration ? 'absolute left-1/2 -translate-x-1/2' : ''}`}>
                            <span className={`text-[10px] uppercase font-semibold tracking-wider mb-0.5 ${'text-[var(--pp-text-secondary)]'}`}>{t('dragdrop.duration')}</span>
                            <MediaDuration item={item} isDarkMode={isDarkMode} />
                        </div>
                        )}

                        {/* -------Standard Controls------- */}
                        {!isCompact && (
                        <div className='flex items-center gap-2 ml-auto flex-shrink-0'>
                             <div className='hidden lg:flex items-center gap-3 border-r pr-3 border-gray-200 dark:border-gray-700'>
                                <div className='flex flex-col items-center gap-1'>
                                    <InputSwitch 
                                        checked={Number(localIsMuted) === 1} 
                                        onChange={toggleMute}
                                        disabled={false}
                                        className="scale-75"
                                        tooltip={Number(localIsMuted) === 1 ? t('dragdrop.unmute') : t('dragdrop.mute')}
                                        tooltipOptions={{ position: 'bottom' }}
                                    />
                                     <span className={`text-[9px] uppercase font-bold tracking-wider ${Number(localIsMuted) === 1 ? 'text-[var(--pp-danger)]' : 'text-[var(--pp-text-secondary)]'}`}>
                                        {Number(localIsMuted) === 1 ? t('dragdrop.muted') : t('dragdrop.sound')}
                                    </span>
                                </div>

                                {(["IMAGE", "VIDEO"].includes(item.type) || !_.isNull(item.playlist_id)) && (
                                    <div className='flex flex-col items-center gap-1'>
                                        <InputSwitch 
                                            checked={Number(localMaintainAspectRatio) === 0} 
                                            onChange={toggleFill}
                                            className="scale-75"
                                            tooltip={Number(localMaintainAspectRatio) === 0 ? t('dragdrop.fit_to_screen') : t('dragdrop.fill_to_screen')}
                                            tooltipOptions={{ position: 'bottom' }}
                                        />
                                         <span className={`text-[9px] uppercase font-bold tracking-wider ${Number(localMaintainAspectRatio) === 0 ? 'text-[var(--pp-btn-honey)]' : 'text-[var(--pp-text-secondary)]'}`}>
                                            {Number(localMaintainAspectRatio) === 0 ? t('dragdrop.fill') : t('dragdrop.fit')}
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="hidden lg:flex scale-90">{actionBodyTemplate(item)}</div>
                            <MobileActions item={item} />
                        </div>
                        )}
                        </div>
                    </div>
                    {/* Active Progress Line */}
                    {isActive && (
                        <div className="absolute bottom-0 left-0 h-[2px] bg-indigo-500 transition-all duration-500 ease-linear"
                            style={{ width: '100%' }}
                        />
                    )}
                </div>
            </div >
            {
                openDialog.mediaSettingPopup && selectedMedia?.campaign_playlist_media_id == item?.campaign_playlist_media_id ?

                    <MediaSettingPopup key={selectedMedia.campaign_playlist_media_id} removeMedia={removeFromCampaign} item={item} setSelectedRow={setSelectedMedia} onPublish={onPublish} />

                    : <></>
            }
            {
                openDialog.playlistSettingPopup && selectedMedia?.campaign_playlist_media_id == item?.campaign_playlist_media_id ?
                    <PlaylistSettingPopup removeMedia={removeFromCampaign} item={selectedMedia} setSelectedRow={setSelectedMedia} /> : <></>
            }


            {openDialog.addCustomFeed ? <AddFeedDialog /> : <></>}
            {openDialog.WeatherDialog ? <WeatherDialog /> : <></>}
            {openDialog.ClockDialog ? <ClockDialog /> : <></>}



        </>
    )
}

export default CampaignCard
